import React, {ReactElement, useState, useEffect, useMemo} from 'react';
import {
  Button,
  Typography,
  DialogContent,
  DialogActions
} from '@customink/pigment-react';
import {
  Product,
  ProductVariantsInner,
  SizesInner
} from '@customink/stores-api-client';
import Dialog from '@mui/material/Dialog';
import Skeleton from '@mui/material/Skeleton';
import XCloseIcon from '../../assets/icons/x_close.svg';
import {
  useGetStoreProductQuery,
  useAddToCartMutation,
  useLazyGetCartQuery
} from '../../api/storesApi';
import services from '../../api/remoteServices';
import {formatCurrency} from '../../utils/helpers';
import './index.scss';

type EstimatedDeliveryDateProps = {
  addedProduct: Product;
};

const EstimatedDeliveryDate: React.FC<EstimatedDeliveryDateProps> = ({
  addedProduct
}: EstimatedDeliveryDateProps) => {
  return addedProduct &&
    addedProduct?.deliveryEstimate &&
    addedProduct.deliveryEstimate !== 'N/A' ? (
    <div className="addToCart-dialog-content-item-details-delivery">
      <Typography variant="captionLarge">Estimated delivery: </Typography>
      <span className="addToCart-dialog-content-item-details-delivery-date">
        <Typography variant="captionLarge">
          {addedProduct.deliveryEstimate}
        </Typography>
      </span>
    </div>
  ) : null;
};

type AddToCartDialogProps = {
  storeSlug: string;
  productId: number;
  currentVariant: ProductVariantsInner | null;
  currentSize: SizesInner | null;
  currentQuantity: number;
  mobile: boolean;
};

const cartUrl = (storeSlug: string) => `${services.stores}/${storeSlug}/cart`;

const AddToCartDialog: React.FC<AddToCartDialogProps> = ({
  storeSlug,
  productId,
  currentVariant,
  currentSize,
  currentQuantity,
  mobile
}: AddToCartDialogProps): ReactElement => {
  const {data: addedProduct} = useGetStoreProductQuery({
    urlSlug: storeSlug,
    productId
  });
  const [open, setOpen] = useState(false);
  const [triggerAddToCartMutation, {isSuccess: addToCartSuccess}] =
    useAddToCartMutation();

  const [trigger, {data: cartDetail, isFetching}] = useLazyGetCartQuery();
  const [lazyCartFetched, setLazyCartFetched] = useState(false);

  useEffect(() => {
    setLazyCartFetched(!isFetching);
  }, [isFetching]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddToCart = () => {
    triggerAddToCartMutation({
      urlSlug: storeSlug,
      id: productId,
      variantId: currentVariant.id,
      variantName: currentVariant.name,
      size: currentSize.value,
      quantity: currentQuantity,
      amount: null
    });
  };

  const handleCartUrl = () => {
    window.location.href = cartUrl(storeSlug);
  };

  useEffect(() => {
    if (addToCartSuccess) {
      setLazyCartFetched(false);
      setOpen(true);
      trigger(storeSlug);
    }
  }, [addToCartSuccess]);

  const formatSubtotal = (subtotalString: string) => {
    const cleanedString = subtotalString.replace(/[$,]/g, '');
    const numberValue = Number(cleanedString);

    if (!Number.isNaN(numberValue)) {
      return formatCurrency(numberValue);
    }
    return subtotalString;
  };

  const addToCartDisabled = useMemo(
    () =>
      !(
        currentQuantity > 0 &&
        currentVariant &&
        currentSize &&
        currentVariant.sizes.findIndex(s => s.value === currentSize.value) !==
          -1
      ),
    [currentQuantity, currentVariant, currentSize]
  );

  const Placeholder = () => {
    const mock = [1, 2];
    return (
      <div className="addToCart-dialog-content-summary-placeholder">
        {mock.map(
          (row: number): ReactElement => (
            <Skeleton
              className="addToCart-dialog-content-summary-placeholder-text"
              variant="text"
              key={row}
            />
          )
        )}
      </div>
    );
  };

  return addedProduct ? (
    <div className="addToCart-dialog">
      <div className="addToCart-button">
        <Button
          disabled={addToCartDisabled}
          className={`addToCartButton${mobile ? '-mobile' : ''}`}
          size="large"
          onClick={handleAddToCart}>
          Add To Cart
        </Button>
      </div>
      <Dialog className="addToCartDialog" open={open} onClose={handleClose}>
        <div className="addToCart-dialog-header">
          <span>
            <Typography variant="bodyShort1">
              Item added to your cart
            </Typography>
          </span>
          <Button
            className="closeButton"
            size="large"
            variant="link"
            onClick={handleClose}>
            <img src={XCloseIcon} alt="Cancel" />
          </Button>
        </div>
        <DialogContent className="addToCart-dialog-content">
          <section className="addToCart-dialog-content-item">
            <div className="addToCart-dialog-content-item-image">
              <img
                src={currentVariant?.primaryImageUrl || addedProduct.imageUrl}
                alt="Product"
              />
            </div>
            <div className="addToCart-dialog-content-item-details">
              <span className="addToCart-dialog-content-item-details-name">
                <Typography variant="captionLarge">
                  <span className="heading">{addedProduct.name}</span>
                </Typography>
              </span>
              <span className="addToCart-dialog-content-item-details-price">
                <Typography variant="captionLarge">
                  {formatCurrency(Number(addedProduct.price))}
                </Typography>
              </span>
              {currentVariant ? (
                <div className="addToCart-dialog-content-item-details-style">
                  <Typography variant="captionLarge">
                    <span className="heading">Style:</span>{' '}
                    {currentVariant.name}
                    {currentSize ? ` - ${currentSize.label}` : ''}
                  </Typography>
                </div>
              ) : null}
              <div className="addToCart-dialog-content-item-details-quantity">
                <Typography variant="captionLarge">
                  <span className="heading">Quantity:</span>{' '}
                  <span>{currentQuantity}</span>
                </Typography>
              </div>
              <EstimatedDeliveryDate addedProduct={addedProduct} />
            </div>
          </section>
          {lazyCartFetched && cartDetail?.cart ? (
            <div className="addToCart-dialog-content-summary">
              <div className="addToCart-dialog-content-summary-items">
                <Typography variant="captionLarge">{`${
                  cartDetail.cart.quantity
                } item${
                  cartDetail.cart.quantity !== 1 ? 's' : ''
                } in your cart`}</Typography>
              </div>
              <div className="addToCart-dialog-content-summary-subtotal">
                <Typography variant="captionLarge">
                  Subtotal: {formatSubtotal(cartDetail.cart.subtotal)}
                </Typography>
              </div>
            </div>
          ) : (
            <Placeholder />
          )}
        </DialogContent>
        <DialogActions className="addToCart-dialog-actions">
          <div className="addToCart-dialog-actions-footer">
            <Button
              className="continueShoppingButton"
              size="large"
              onClick={handleClose}>
              Continue Shopping
            </Button>
            <Button
              className="goToCartButton"
              size="large"
              onClick={handleCartUrl}>
              Go to Cart
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  ) : null;
};

export default AddToCartDialog;
